import { useContext, useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { createProductSpecificAnalyticsIntegrationPayload } from '@atlassian/growth-commons-error-analytics/services/create-product-specific-analytics-integration-payload';
import {
	ErrorType,
	Severity,
	Product,
} from '@atlassian/growth-commons-error-analytics/constants/event-attributes';
import { Team } from '@atlassian/growth-commons-error-analytics/constants/team';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ConfluenceEdition } from '../../Upgrade/__types__/waitForEditionChangeQuery.ts';

import type {
	useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery as useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$data,
	useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQueryVariables as useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$variables,
} from './__types__/useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
const extractBillingAdminStatus = (
	billingData: useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$data | undefined,
): boolean | null | undefined => {
	return billingData?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.transactionAccount
		?.isCurrentUserBillingAdmin;
};

export const useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment = ({
	skip = false,
	currentEdition,
	targetEdition,
}: {
	skip: boolean;
	currentEdition: ConfluenceEdition | null;
	targetEdition: ConfluenceEdition | null;
}): {
	isEligible: boolean;
	isSiteAdmin: boolean;
	isBillingAdmin: boolean | null | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const isBasicEligible = Boolean(
		cloudId &&
		currentEdition === ConfluenceEdition.FREE &&
		targetEdition === ConfluenceEdition.STANDARD &&
		isSiteAdmin,
	);

	const {
		data: billingAdminData,
		loading: isBillingAdminQueryLoading,
		error: billingAdminQueryError,
	} = useQueryInterop<
		useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$data,
		useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$variables
	>(
		graphql`
			query useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery($cloudId: ID!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlement {
							id
							transactionAccount {
								... on Node {
									id
									__typename
								}
								isCurrentUserBillingAdmin
							}
						}
					}
				}
			}
		`,
		{
			interopApolloAggClient: true,
			variables: { cloudId },
			skip: skip || !isBasicEligible,
		},
	);

	useEffect(() => {
		if (billingAdminQueryError) {
			const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
				product: Product.CONFLUENCE,
				team: Team.GROWTH_LUNA,
				experimentMeta: {
					experimentKey: 'grw-1930_confluence_upv2_standard_ab',
				},
				errorType: ErrorType.GRAPHQL,
				errorMessage: billingAdminQueryError.message,
				source: 'useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment hook',
				severity: Severity.HIGH,
			});

			createAnalyticsEvent(eventPayload).fire();
		}
	}, [billingAdminQueryError, createAnalyticsEvent]);

	const isBillingAdmin = useMemo(() => {
		return extractBillingAdminStatus(billingAdminData);
	}, [billingAdminData]);

	return {
		isEligible: Boolean(isBasicEligible && isBillingAdmin),
		isSiteAdmin,
		isBillingAdmin,
		loading: isBillingAdminQueryLoading,
		error: billingAdminQueryError,
	};
};
